import React, { useState } from 'react';

/**
 * MiCoachLogo
 * -----------------------------------------------------------------------------
 * Renderiza el isotipo de MiCoach usando el logo VECTORIZADO (SVG) oficial.
 *
 * Estrategia:
 *   1. Se dibuja un "badge" (fondo redondeado) y, dentro, la marca en SVG.
 *      El SVG es vectorial → nítido a cualquier tamaño y en pantallas retina.
 *   2. Si el SVG no carga (404 o error), cae al SVG embebido de respaldo
 *      (FallbackMark) para que la interfaz nunca se rompa visualmente.
 *
 * Aspecto (igual que antes): badge esmeralda + marca BLANCA en ambos temas.
 * Solo la variante sin fondo (withBackground=false) adapta el color de la
 * marca al tema (blanca en oscuro, verde en claro).
 *
 * Archivos SVG en /frontend/public:
 *   - /micoach-logo-blanco.svg  → marca blanca (para badge verde / fondos oscuros)
 *   - /micoach-logo-verde.svg   → marca verde  (para fondos claros sin badge)
 *   - /micoach-logo-negro.svg   → marca negra  (uso puntual sobre fondos claros)
 *
 * Nota: el PDF (jsPDF) y los iconos PWA/favicon usan versiones PNG del mismo
 * logo a propósito, porque esos contextos NO admiten SVG (son raster).
 *
 * Props:
 *   - size: tamaño en píxeles (cuadrado). Default 32.
 *   - variant: 'on-emerald' | 'on-dark' | 'on-light' | 'auto'. Default 'auto'.
 *   - rounded: radio del badge. Default ≈ size * 0.25.
 *   - withBackground: pintar el badge (true) o solo la marca (false).
 *   - className / style: passthrough al wrapper.
 */
const MARK_COLORS = {
  emeraldBg: '#10B981',
  darkBg: '#0A0F0C',
  lightBg: '#FFFFFF',
  whiteStroke: '#FFFFFF',
  emeraldStroke: '#10B981',
  emeraldStrokeLight: '#059669',
};

// SVG vectorizados oficiales en /frontend/public.
const SVG = {
  blanco: '/micoach-logo-blanco.svg',
  verde: '/micoach-logo-verde.svg',
  negro: '/micoach-logo-negro.svg',
};

const resolveVariant = (variant) => {
  if (variant !== 'auto') return variant;
  if (typeof document !== 'undefined' &&
      document.documentElement.getAttribute('data-theme') === 'light') {
    return 'on-light';
  }
  return 'on-dark';
};

const FallbackMark = ({ size, variant, rounded, withBackground, ariaLabel }) => {
  let bg;
  let stroke;
  switch (variant) {
    case 'on-emerald':
      bg = MARK_COLORS.emeraldBg;
      stroke = MARK_COLORS.whiteStroke;
      break;
    case 'on-light':
      bg = MARK_COLORS.lightBg;
      stroke = MARK_COLORS.emeraldStrokeLight;
      break;
    case 'on-dark':
    default:
      bg = MARK_COLORS.darkBg;
      stroke = MARK_COLORS.emeraldStroke;
      break;
  }
  const radius = rounded ?? Math.round(size * 0.25);
  const innerSize = Math.round(size * 0.66);
  const strokeWidth = Math.max(6, Math.round(100 * (8 / size)) || 9);

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: withBackground ? bg : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg
        width={innerSize}
        height={innerSize}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 82 V40 C18 26, 36 26, 36 40 V60" />
          <path d="M36 60 C36 74, 64 74, 64 60" />
          <path d="M82 82 V40 C82 26, 64 26, 64 40 V60" />
        </g>
      </svg>
    </span>
  );
};

export default function MiCoachLogo({
  size = 32,
  variant = 'auto',
  rounded,
  withBackground = true,
  className = '',
  style = {},
  ariaLabel = 'MiCoach',
}) {
  const v = resolveVariant(variant);
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <FallbackMark
        size={size}
        variant={v}
        rounded={rounded}
        withBackground={withBackground}
        ariaLabel={ariaLabel}
      />
    );
  }

  const radius = rounded ?? Math.round(size * 0.25);

  // Color del badge: esmeralda cuando hay fondo (igual que ahora).
  const badgeBg = MARK_COLORS.emeraldBg;

  // Color de la marca (SVG):
  //  - Con badge  → siempre blanca (marca blanca sobre verde).
  //  - Sin badge  → blanca en tema oscuro, verde en tema claro.
  let markSrc;
  if (withBackground) {
    markSrc = SVG.blanco;
  } else {
    markSrc = v === 'on-light' ? SVG.verde : SVG.blanco;
  }

  // La marca ocupa ~58% del badge y se centra (el SVG conserva su proporción).
  const markSize = Math.round(size * 0.58);

  return (
    <span
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: withBackground ? badgeBg : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        ...style,
      }}
    >
      <img
        src={markSrc}
        alt={ariaLabel}
        onError={() => setErrored(true)}
        style={{
          height: markSize,
          width: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </span>
  );
}
